import Layout from "../Layout";
import React, { useState, useEffect, useRef } from "react";
import Cookies from 'js-cookie';
import CryptoJS from 'crypto-js';
import axios from 'axios';
import { QRCodeSVG } from "qrcode.react";
import { LockKeyhole } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// --- IMPORTANT: Store this key securely, do NOT hardcode in production ---
const SECRET_COOKIE_KEY = "mySuperSecretCookieKey123!@#"; // Must match Register.js

// Helper functions for encryption/decryption
const encrypt = (text) => {
  return CryptoJS.AES.encrypt(text, SECRET_COOKIE_KEY).toString();
};

const decrypt = (ciphertext) => {
  try {
    const bytes = CryptoJS.AES.decrypt(ciphertext, SECRET_COOKIE_KEY);
    return bytes.toString(CryptoJS.enc.Utf8);
  } catch (e) {
    console.error("Error decrypting cookie:", e);
    return null;
  }
};

const API_BASE_URL = 'https://stage.beachcabana.lk/hotelmanage/api.php';
const AUTH_HEADER = {
  headers: {
    'Authorization': 'Basic YWRtaW46cGFzc3dvcmQ=' // admin:password encoded to Base64
  }
};

export default function Menu() {
  const [showTokenPopup, setShowTokenPopup] = useState(false);
  const [tokenInput, setTokenInput] = useState(""); // Single string for the full token
  const [tokenError, setTokenError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [isFetchingWifi, setIsFetchingWifi] = useState(false);
  const [wifiData, setWifiData] = useState(null);

  // Create an array of refs for the 5 input fields
  const inputRefs = useRef([]);
  // Initialize the refs array with nulls, it will be populated when inputs render
  if (inputRefs.current.length !== 5) {
    inputRefs.current = Array(5).fill(null).map((_, i) => inputRefs.current[i] || React.createRef());
  }

  const navigate = useNavigate();

  // Check device registration and token on mount
  useEffect(() => {
    const checkDeviceRegistration = async () => {
      const encryptedDeviceRegistered = Cookies.get('deviceRegistered');
      const encryptedregistrationToken = Cookies.get('registrationToken');
      const encryptedRegistrationExpiry = Cookies.get('registrationExpiry');

      if (encryptedDeviceRegistered && encryptedregistrationToken && encryptedRegistrationExpiry) {
        const deviceRegistered = decrypt(encryptedDeviceRegistered);
        const registrationToken = decrypt(encryptedregistrationToken);
        const registrationExpiry = decrypt(encryptedRegistrationExpiry);

        if (deviceRegistered === 'true' && registrationToken && registrationExpiry) {
          const expiryDate = new Date(registrationExpiry);
          if (new Date() < expiryDate) {
            setIsRegistered(true);
            console.log("Device is registered with valid token.");
            fetchWifiDetails();
          } else {
            Cookies.remove('deviceRegistered');
            Cookies.remove('registrationToken');
            Cookies.remove('registrationExpiry');
            Cookies.remove('currentRegistrationId');
            setIsRegistered(false);
            setShowTokenPopup(true);
            console.log("Registration expired. Prompting for token.");
          }
        } else {
          Cookies.remove('deviceRegistered');
          Cookies.remove('registrationToken');
          Cookies.remove('registrationExpiry');
          Cookies.remove('currentRegistrationId');
          setIsRegistered(false);
          setShowTokenPopup(true);
          console.log("Invalid cookies. Prompting for token.");
        }
      } else {
        setIsRegistered(false);
        setShowTokenPopup(true);
        console.log("No registration cookies found. Prompting for token.");
      }
    };

    checkDeviceRegistration();
  }, []);

  const fetchWifiDetails = async () => {
    setIsFetchingWifi(true);
    setFetchError(null);
    try {
      const response = await axios.get(`${API_BASE_URL}?param=wifi`, AUTH_HEADER);
      if (response.data.code === 200 && response.data.data) {
        setWifiData(response.data.data);
      } else {
        setFetchError("Failed to load WiFi details. Please try again later.");
      }
    } catch (err) {
      console.error("Error fetching WiFi details:", err);
      setFetchError("Failed to load WiFi details. Please try again later.");
    } finally {
      setIsFetchingWifi(false);
    }
  };

  // Handler for individual input changes
  const handleChange = (e, index) => {
    const { value } = e.target;
    if (value.length > 1 || (value && !/^[a-zA-Z0-9]$/.test(value))) {
      return;
    }

    // Initialize token array with existing characters or empty strings
    const newTokenArray = Array(5).fill('');
    for (let i = 0; i < 5 && i < tokenInput.length; i++) {
      newTokenArray[i] = tokenInput[i] || '';
    }
    // Update the current index with the new value
    newTokenArray[index] = value.toUpperCase();
    const updatedToken = newTokenArray.join('');

    console.log(`handleChange: index=${index}, value=${value}, updatedToken=${updatedToken}, tokenInput=${tokenInput}`); // Debug log

    // Update state
    setTokenInput(updatedToken);
    setTokenError(""); // Clear error on input change

    // Move focus to the next input
    if (value && index < 4) {
      inputRefs.current[index + 1]?.focus();
    }

    // Trigger automatic submission if token is exactly 5 characters
    if (updatedToken.length === 5) {
      console.log("Triggering automatic submission for token:", updatedToken);
      handleTokenSubmit(null, updatedToken); // Pass updatedToken directly
    }
  };

  // Handler for keyboard navigation and backspace
  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace') {
      const newTokenArray = Array(5).fill('');
      for (let i = 0; i < 5 && i < tokenInput.length; i++) {
        newTokenArray[i] = tokenInput[i] || '';
      }
      if (tokenInput[index]) {
        newTokenArray[index] = '';
        setTokenInput(newTokenArray.join(''));
      } else if (index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
      setTokenError(""); // Clear error on backspace
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 4) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleTokenSubmit = async (e, token = tokenInput) => {
    // Prevent default form submission if triggered by a form event
    if (e) {
      e.preventDefault();
    }

    setIsLoading(true);
    setTokenError("");

    // Use the provided token (from handleChange) or fallback to tokenInput
    if (token.length !== 5) {
      console.log("Submission failed: token=", token, "tokenInput=", tokenInput); // Debug log
      setTokenError("Please enter all 5 characters of the token.");
      setIsLoading(false);
      return;
    }

    console.log("Submitting token:", token); // Debug log

    try {
      const response = await axios.post(`${API_BASE_URL}?param=tokenvalidate`, { token }, AUTH_HEADER);

      if (response.data.code === 200 && response.data.data) {
        const registrationData = response.data.data;
        const expiryDate = new Date(registrationData.registrationExpiresAt);

        if (new Date() < expiryDate) {
          Cookies.set('deviceRegistered', encrypt('true'), { expires: expiryDate });
          Cookies.set('registrationToken', encrypt(token), { expires: expiryDate });
          Cookies.set('registrationExpiry', encrypt(expiryDate.toISOString()), { expires: expiryDate });
          Cookies.set('currentRegistrationId', encrypt(registrationData.registrationToken), { expires: expiryDate });

          setIsRegistered(true);
          setShowTokenPopup(false);          
          setTokenInput(""); // Clear token input after successful validation
          console.log("Token verified successfully. Cookies set.");
          fetchWifiDetails();
        } else {
          setTokenError("This token has expired. Please register again or use a valid token.");
        }
      } else {
        setTokenError(response.data.message || "Invalid token. Please check and try again.");
      }
    } catch (err) {
      console.error("Error verifying token:", err);
      setTokenError("Error verifying token. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  // Define ssid, password, and wifiQR here, so they are available for QRCodeSVG
  const ssid = wifiData ? wifiData.ssid : '';
  const password = wifiData ? wifiData.password : '';
  const wifiQR = `WIFI:T:WPA;S:${ssid};P:${password};;`;

  return (
    <Layout pageTitle="WiFi Details">
      {/* "Hello World" div when not registered */}
      {!isRegistered && (
        <div className="text-center text-xl font-bold text-gray-700">
          WiFi Loading...
        </div>
      )}

      <div className={`${isRegistered ? '' : 'opacity-50 pointer-events-none'}`}>
        {isRegistered && isFetchingWifi && (
          <div className="text-center py-8">
            <p className="text-lg text-gray-600">Loading WiFi details...</p>
          </div>
        )}
        {isRegistered && fetchError && (
          <div className="text-center py-8">
            <p className="text-lg text-red-500">{fetchError}</p>
          </div>
        )}
        {isRegistered && !isFetchingWifi && !fetchError && !wifiData && (
          <div className="text-center py-8">
            <p className="text-lg text-gray-600">No WiFi details available.</p>
          </div>
        )}

        {isRegistered && !isFetchingWifi && !fetchError && wifiData && (
          <div className="text-center">
            <div>
          <p className="text-md text-gray-600 mb-5">Scan the QR code or enter details manually below</p> {/* Clearer instruction */}
        </div>


        <div className="space-y-2 mb-5">
          <div className="text-sm font-medium">
            <span className="font-bold text-gray-500">SSID:</span>
            <h3 className="text-lg font-mono">{wifiData.ssid}</h3>
          </div>

          <div className="text-sm font-medium">
            <span className="font-bold text-gray-500">Password:</span>
            <h3 className="text-lg font-mono">{wifiData.password}</h3>
          </div>
        </div>

            <div className="flex justify-center">
              <QRCodeSVG value={wifiQR} size={200} className="mt-3 mb-3" />
            </div>

            <p className="text-sm text-gray-500 mt-6">
          Most modern smartphones can scan this QR code directly using the camera app.
        </p>
          </div>
        )}
      </div>

      {/* Token Verification Popup */}
      {showTokenPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75">
          <div className="bg-white w-full max-w-sm mx-auto rounded-lg shadow-lg p-6 text-center">
            {/* Centered LockKeyhole icon */}
            <div className="flex justify-center mb-4">
              <LockKeyhole className="w-8 h-8 text-blue-600" />
            </div>

            <h3 className="text-xl font-bold mb-4 text-gray-800">Enter Registration Token</h3>
            <p className="text-gray-700 mb-6">
              This is the first 5 characters of your Passport number or national ID number provided during registration.
            </p>
            <form onSubmit={handleTokenSubmit}>
              <div className="flex justify-center gap-2 mb-4">
                {[...Array(5)].map((_, index) => (
                  <input
                    key={index}
                    type="text"
                    maxLength={1}
                    value={tokenInput[index] || ''}
                    onChange={(e) => handleChange(e, index)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    ref={(el) => (inputRefs.current[index] = el)}
                    className="w-10 h-10 text-center text-lg font-semibold border rounded-lg border-gray-300 p-0"
                    autoFocus={index === 0}
                  />
                ))}
              </div>
              {tokenError && (
                <p className="text-sm text-red-500 mb-4">{tokenError}</p>
              )}

              <div className="text-center">
                <button
                  type="submit"
                  className={`px-6 py-2 w-full bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 ${
                    isLoading || tokenInput.length !== 5 ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  disabled={isLoading || tokenInput.length !== 5}
                >
                  {isLoading ? "Verifying..." : "Submit"}
                </button>

                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="px-6 py-2 w-full bg-white text-gray-900 font-medium text-sm rounded-lg hover:bg-gray-100 mt-3"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}